import { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import { GenericChart, GenericChartProps } from 'app/agriculture/charts/GenericChart/GenericChart';
import { Line } from 'react-chartjs-2';
import { isEqual, startOfHour } from 'date-fns';
import { useTimelineCursorTime } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { getDefaultLineChartOptions } from 'app/agriculture/charts/LineChart/DefaultLineChartOptions';
import { EmptyLineChart } from 'app/agriculture/charts/EmptyLineChart/EmptyLineChart';
import { merge } from 'lodash';
import { ChartContext } from 'app/agriculture/charts/ChartContextProvider';
import { useContext } from 'react';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { primaryColourMain } from 'app/agriculture/material';

function backgroundColor(context: ScriptableContext<'line'>): CanvasGradient {
    const { ctx: canvas } = context.chart;
    const gradient = canvas.createLinearGradient(0, 0, 0, context.chart.height);
    gradient.addColorStop(0, primaryColourMain);
    gradient.addColorStop(0.8, `${primaryColourMain}00`);
    return gradient;
}

export function LineChart({
    data,
    title,
    displayUnit,
    showCurrentValue = true,
    chartOptionsOverride,
}: GenericChartProps & {
    chartOptionsOverride?: Partial<ChartOptions<'line'>>;
}) {
    const { intervalUnit, dateFormat } = useContext(ChartContext);
    const { timeZone } = useTimeZone();
    const cursorTime = useTimelineCursorTime() ?? new TZDate(new Date(), timeZone);
    const currentHour = startOfHour(cursorTime);
    // The regex removes milliseconds. See: https://stackoverflow.com/a/65076090
    const currentValue = data[currentHour.toISOString().replace(/[.]\d+/, '')];
    const values = Object.values(data);
    const chartIsEmpty = values.every((value) => isNaN(value));

    const chartData: ChartData<'line'> = {
        labels: Object.keys(data),
        datasets: [
            {
                data: Object.values(data),
                fill: true,
                pointStyle: 'circle',
                spanGaps: false,
                borderColor: primaryColourMain,
                pointRadius: ({ dataIndex }) => {
                    const timestamp = Object.keys(data)[dataIndex];
                    if (timestamp == null) {
                        throw Error(`No matching timestamp for ${dataIndex}`);
                    }
                    const indexDate = new Date(timestamp);
                    if (isEqual(indexDate, currentHour)) {
                        return 8;
                    } else {
                        if (values[dataIndex - 1] == null && values[dataIndex + 1] == null) {
                            return 4;
                        } else {
                            return 0;
                        }
                    }
                },
                pointBorderWidth: 3,
                pointBorderColor: 'white',
                pointBackgroundColor: primaryColourMain,
                tension: 0,
                backgroundColor,
            },
        ],
    };

    const chartOptions = merge(
        getDefaultLineChartOptions({
            intervalUnit: intervalUnit,
            yMeasurementUnit: displayUnit,
            dateFormat,
            timeZone,
        }),
        chartOptionsOverride,
    );

    return chartIsEmpty ? (
        <EmptyLineChart title={title} />
    ) : (
        <GenericChart title={title}>
            {showCurrentValue ? (
                <h5>
                    {currentValue == null || isNaN(currentValue)
                        ? 'Unknown'
                        : `${currentValue}${displayUnit}`}
                </h5>
            ) : null}
            <Line data={chartData} options={chartOptions} />
        </GenericChart>
    );
}
